import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IosShareIcon from "@mui/icons-material/IosShare";
import ReplayIcon from "@mui/icons-material/Replay";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/system/Box";
import { format, formatDistanceToNow } from "date-fns";
import Link from "next/link";
import { ChangeEvent, FC, MouseEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getMainKeyword } from "src/slices/brief/helpers";
import { selectLoaders } from "src/slices/brief/selectors";
import {
  archiveBriefRequested,
  getAllBriefData,
  unarchiveBriefRequested,
} from "src/slices/brief/thunks";
import { BriefType } from "src/slices/brief/types";
import { calculateSuggestedData } from "src/slices/suggestions/helpers";
import { snackAdded } from "src/slices/system";
import { useDispatch } from "src/store";
import { generateBriefText } from "src/utils/rtf";
import ReassignBriefDialog from "./projects/ReassignBriefDialog";
// Article Stuff
import { useRouter } from "next/router";
import { EditPencil } from "src/icons/edit-pencil";
import { getArticle } from "src/slices/articles/thunks";
import { getActivePlan } from "src/slices/auth/thunks";
import { getProjectById } from "src/slices/projects";
import { AddPaymentModal } from "./addPaymenModal";

type Props = {
  briefs: Array<
    Omit<BriefType, "status"> & {
      status: string;
    }
  >;
  areBriefsLoading: boolean;
  total: number;
  handlePageChange: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRetry: ({
    keyword,
    id,
    locationName,
  }: Omit<BriefType, "status">) => void;
  handleRetryArticle: (id: string) => void;
  page: number;
  limit: number;
  tab: string;
  handleDelete: (id: string) => void;
  articleProgress: Record<
    string,
    {
      status: "processing" | "completed" | "failed";
      current: number;
      total: number;
      percentage: number;
    }
  >;
  briefProgress: Record<
    string,
    {
      stepRemains: number;
      articleStatus: string;
    }
  >;
};

const DashboardBriefTable: FC<Props> = ({
  briefs,
  areBriefsLoading,
  total,
  handlePageChange,
  handleRowsPerPageChange,
  handleRetry,
  handleRetryArticle,
  page,
  limit,
  tab,
  handleDelete,
  articleProgress,
  briefProgress,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const loaders = useSelector(selectLoaders);
  const [IsReassignBriefDialogOpen, setIsReassignBriefDialogOpen] =
    useState("");
  const [briefName, setBriefName] = useState("");
  const [briefId, setBriefId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userPlan, setUserPlan] = useState<any>({});
  const [hideArticle, setHideArticle] = useState(false);
  const [deletingBrief, setDeletingBrief] = useState<string | null>(null);
  const [displayBriefs, setDisplayBriefs] = useState<Array<any>>([]);

  // Effect to manage display briefs and animation
  useEffect(() => {
    if (displayBriefs.length === 0) {
      // Initialize display briefs
      setDisplayBriefs(briefs);
      return;
    }

    // Check if a brief was removed
    if (briefs.length < displayBriefs.length) {
      const deletedBrief = displayBriefs.find(
        (displayBrief) => !briefs.find((brief) => brief.id === displayBrief.id)
      );

      if (deletedBrief) {
        // Start animation for deleted brief
        setDeletingBrief(deletedBrief.id);

        // After animation completes, remove from display list
        setTimeout(() => {
          setDisplayBriefs(briefs);
          setDeletingBrief(null);
        }, 300);
        return;
      }
    }

    // For other changes (additions, updates), update immediately
    setDisplayBriefs(briefs);
  }, [briefs]);

  const handleArchive = (id: string) => {
    dispatch(archiveBriefRequested({ id }));
  };
  const handleUnarchive = (id: string) => {
    dispatch(unarchiveBriefRequested({ id }));
  };
  const Project = useSelector(getProjectById(briefId));
  const getUserPlan = async () => {
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    if (currentPlan) {
      setUserPlan(currentPlan);
    }
  };
  const handleCopyToClipBoard = async (id: string) => {
    const result = await dispatch(getAllBriefData(id)).unwrap();

    const { wordCount, headings, images } = calculateSuggestedData(
      result.data.competitors
    );
    const brief = briefs.find((brief) => brief.id === id) as BriefType;
    setBriefId(brief?.id);

    const mainKeywordSearchVolume = getMainKeyword({
      competitors: result.data.competitors,
      keyword: brief.keyword,
    });
    const text = generateBriefText(
      {
        brief: {
          ...brief,
          mainKeywordSearchVolume,
          wordCount: brief.wordCount === 0 ? wordCount : brief.wordCount,
          headings: brief.headings ?? headings,
          images: brief.images ?? images,
        },
      },
      { projectData: Project }
    );

    const item = new ClipboardItem({
      "text/html": new Blob([text], { type: "text/html" }) as any,
    });

    await navigator.clipboard.write([item]);

    dispatch(
      snackAdded({
        message: "Copied to clipboard",
        type: "success",
        duration: 1000,
      })
    );
  };

  const getIsCopyToClipboardGenerating = (id: string) =>
    loaders[id]?.["briefs/briefAsync"] === "pending";

  // Article Stuff

  useEffect(() => {
    getUserPlan();
  }, []);

  const handleDownloadArticle = async (
    id: string,
    title: string | undefined
  ) => {
    const result = await dispatch(getArticle({ id: id }));
    const data = result.payload?.data;
    const brief = briefs.find((brief) => brief.id === id) as BriefType;

    let hideArticle = false;

    if (
      Object.keys(userPlan).length === 0 &&
      brief?.isOnboardingBrief &&
      !brief?.isSample
    ) {
      hideArticle = true;
      setHideArticle(true);
    } else {
      hideArticle = false;
      setHideArticle(false);
    }

    if (data === null) {
      dispatch(
        snackAdded({
          message: "Article is not ready to download",
          type: "error",
          duration: 2500,
        })
      );
      return;
    }
    if (data.status !== "completed") {
      dispatch(
        snackAdded({
          message: "Article is not ready to download",
          type: "error",
          duration: 2500,
        })
      );
      return;
    }

    const url = `/app/dashboard/articleEditor/?id=${encodeURIComponent(
      brief.id
    )}&title=${encodeURIComponent(
      brief.title || ""
    )}&description=${encodeURIComponent(
      brief.description || ""
    )}&isOnboardingBrief=${brief.isOnboardingBrief}&hideArticle=${hideArticle}`;

    window.location.href = url;
  };
  const isWithinInterval = (brief, startDate: Date) => {
    //write a function that checks if startdate is greater then 8 mins from now
    const now = new Date();
    const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60000);
    startDate = new Date(startDate);
    if (startDate.getTime() < fifteenMinutesAgo.getTime()) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        location={router.pathname}
      />
      <ReassignBriefDialog
        id={IsReassignBriefDialogOpen}
        isOpen={Boolean(IsReassignBriefDialogOpen)}
        setIsReassignBriefDialogOpen={setIsReassignBriefDialogOpen}
        briefName={briefName}
        projectId={projectId}
        tab={tab}
      />
      <TableContainer>
        <Table aria-label="simple table">
          <TableHead
            sx={{
              backgroundColor: "secondary.main",
              p: "2rem 0.5rem",
            }}
          >
            <TableRow>
              {/* projects commented out */}
              <TableCell sx={{ padding: "1.5rem 1rem" }}>
                Project Name
              </TableCell>
              <TableCell sx={{ padding: "1.5rem 1rem" }}>Created</TableCell>
              <TableCell>Last modified</TableCell>
              <TableCell>Keyword</TableCell>
              <TableCell>Location</TableCell>
              <TableCell sx={{ width: "255px" }}>Actions</TableCell>
              <TableCell>Articles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areBriefsLoading ? (
              <TableRow sx={{ height: "73px" }}>
                <TableCell colSpan={7}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              displayBriefs?.map((brief) => (
                <Fade
                  key={brief.id}
                  in={deletingBrief !== brief.id}
                  timeout={300}
                >
                  <TableRow>
                    {/* projects commented out */}
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        borderBottom: "1px solid #E6E8F0",
                        color: "#121828",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          position: "relative",
                          "&:hover": {
                            "& #editPencil": {
                              display: "block",
                            },
                          },
                        }}
                        onClick={() => [
                          setIsReassignBriefDialogOpen(brief.id),
                          setBriefName(brief.keyword),
                          setProjectId(brief.projectId),
                        ]}
                      >
                        <Box sx={{}}>
                          {brief?.projectName
                            ? brief.projectName
                            : "Unassigned"}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <EditPencil
                            id="editPencil"
                            sx={{
                              ml: "0.3rem",
                              mt: "0.5rem",

                              // ml: "5rem",

                              position: "absolute",

                              zIndex: 1,

                              display: "none",
                            }}
                            onClick={() => [
                              setIsReassignBriefDialogOpen(brief.id),
                              setBriefName(brief.keyword),
                              setProjectId(brief.projectId),
                            ]}
                          />
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {brief.createdAt
                        ? format(new Date(brief.createdAt), "MMM dd, yyyy")
                        : ""}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {brief.updatedAt
                        ? formatDistanceToNow(new Date(brief.updatedAt))
                        : ""}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {brief?.isSample
                        ? `[Sample] ${brief.keyword}`
                        : brief.keyword}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {brief.locationName}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {briefProgress[brief.id]?.stepRemains > 0 &&
                      !brief?.isSample &&
                      brief?.retry == false &&
                      isWithinInterval(brief, new Date(brief?.createdAt)) ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ width: "80%" }}>
                            {Math.round(
                              ((8 -
                                (briefProgress[brief.id]?.stepRemains !==
                                undefined
                                  ? briefProgress[brief.id].stepRemains
                                  : brief.stepRemains)) /
                                8) *
                                100
                            ) === 0 ? (
                              <>
                                <Box sx={{ width: "80%" }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.round((5 / 100) * 100)}
                                  />
                                </Box>
                                <Box sx={{ ml: "0.5rem", width: "20%" }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {Math.round((5 / 100) * 100)}%
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <Box sx={{ width: "80%" }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.round(
                                        ((8 -
                                          (briefProgress[brief.id]
                                            ?.stepRemains !== undefined
                                            ? briefProgress[brief.id]
                                                .stepRemains
                                            : brief.stepRemains)) /
                                          8) *
                                          100
                                      )}
                                    />
                                  </Box>
                                  <Box sx={{ ml: "0.5rem", width: "20%" }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {Math.round(
                                        ((8 -
                                          (briefProgress[brief.id]
                                            ?.stepRemains !== undefined
                                            ? briefProgress[brief.id]
                                                .stepRemains
                                            : brief.stepRemains)) /
                                          8) *
                                          100
                                      )}
                                      %
                                    </Typography>
                                  </Box>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Box>
                      ) : ((briefProgress[brief.id]?.stepRemains > 0 ||
                          brief?.stepRemains > 0) &&
                          brief?.retry == true) ||
                        (!isWithinInterval(brief, new Date(brief?.createdAt)) &&
                          briefProgress[brief.id]?.stepRemains > 0) ? (
                        <Box display="flex">
                          <Link href="">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={async () => {
                                handleRetry(brief);
                              }}
                            >
                              Retry
                            </Button>
                          </Link>
                          <IconButton
                            aria-label="archive"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() => handleCopyToClipBoard(brief.id)}
                            disabled={getIsCopyToClipboardGenerating(brief.id)}
                          >
                            <Tooltip title="Copy Brief to Clipboard">
                              {getIsCopyToClipboardGenerating(brief.id) ? (
                                <CircularProgress size={20} />
                              ) : (
                                <IosShareIcon />
                              )}
                            </Tooltip>
                          </IconButton>
                          <IconButton
                            aria-label="archive"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() =>
                              tab === "active"
                                ? handleArchive(brief.id)
                                : handleUnarchive(brief.id)
                            }
                          >
                            {tab === "active" ? (
                              <Tooltip title="Archive">
                                <ArchiveOutlinedIcon />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Unarchive">
                                <ReplayIcon />
                              </Tooltip>
                            )}
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() => handleDelete(brief.id)}
                          >
                            <Tooltip title="Delete">
                              <DeleteOutlineIcon />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex">
                          <Link href={`/brief/${brief.id}`} passHref>
                            <Button variant="outlined" color="primary">
                              Open
                            </Button>
                          </Link>
                          <IconButton
                            aria-label="archive"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() => handleCopyToClipBoard(brief.id)}
                            disabled={getIsCopyToClipboardGenerating(brief.id)}
                          >
                            <Tooltip title="Copy Brief to Clipboard">
                              {getIsCopyToClipboardGenerating(brief.id) ? (
                                <CircularProgress size={20} />
                              ) : (
                                <IosShareIcon />
                              )}
                            </Tooltip>
                          </IconButton>
                          <IconButton
                            aria-label="archive"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() =>
                              tab === "active"
                                ? handleArchive(brief.id)
                                : handleUnarchive(brief.id)
                            }
                          >
                            {tab === "active" ? (
                              <Tooltip title="Archive">
                                <ArchiveOutlinedIcon />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Unarchive">
                                <ReplayIcon />
                              </Tooltip>
                            )}
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            sx={{ ml: 1, color: "#7B85AD" }}
                            onClick={() => handleDelete(brief.id)}
                          >
                            <Tooltip title="Delete">
                              <DeleteOutlineIcon />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ borderBottom: "1px solid #E6E8F0" }}
                    >
                      {/* Article is completed from subscription data */}
                      {articleProgress[brief.id]?.status === "completed" && (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            component="a"
                            sx={{
                              border: "none",
                              outline: "none",
                              borderRadius: "10px",
                              background:
                                "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
                              position: "relative",
                              zIndex: "1",
                              "&::before": {
                                content: "''",
                                position: "absolute",
                                top: "1px",
                                right: "1px",
                                bottom: "1px",
                                left: "1px",
                                backgroundColor: "#fff",
                                borderRadius: "9px",
                                zIndex: "-1",
                              },
                              "&::after": {
                                content: "'Open AI Article'",
                                background:
                                  "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
                                WebkitBackgroundClip: "text",
                                color: "transparent",
                              },
                              "&:hover": {
                                border: "0px",
                              },
                            }}
                            onClick={async (e) => {
                              // Only prevent default for left-click to allow right-click context menu
                              if (e.button === 0) {
                                e.preventDefault();
                                await handleDownloadArticle(
                                  brief.id,
                                  brief.title
                                );
                              }
                            }}
                            onAuxClick={async (e) => {
                              // Middle mouse button click
                              if (e.button === 1) {
                                e.preventDefault();

                                // Get the brief data
                                const result = await dispatch(
                                  getArticle({ id: brief.id })
                                );
                                const data = result.payload?.data;

                                // Calculate hideArticle value using the same logic as handleDownloadArticle
                                let hideArticle = false;
                                if (
                                  Object.keys(userPlan).length === 0 &&
                                  brief?.isOnboardingBrief &&
                                  !brief?.isSample
                                ) {
                                  hideArticle = true;
                                }

                                // Check if article is ready
                                if (
                                  data === null ||
                                  (data && data.status !== "completed")
                                ) {
                                  dispatch(
                                    snackAdded({
                                      message:
                                        "Article is not ready to download",
                                      type: "error",
                                      duration: 2500,
                                    })
                                  );
                                  return;
                                }

                                // Open in new tab with the correct hideArticle value
                                window.open(
                                  `/app/dashboard/articleEditor/?id=${encodeURIComponent(
                                    brief.id
                                  )}&title=${encodeURIComponent(
                                    brief.title || ""
                                  )}&description=${encodeURIComponent(
                                    brief.description || ""
                                  )}&isOnboardingBrief=${
                                    brief.isOnboardingBrief
                                  }&hideArticle=${hideArticle}`,
                                  "_blank"
                                );
                              }
                            }}
                            // Add href to enable right-click "Open in new tab" browser functionality
                            href={`/app/dashboard/articleEditor/?id=${encodeURIComponent(
                              brief.id
                            )}&title=${encodeURIComponent(
                              brief.title || ""
                            )}&description=${encodeURIComponent(
                              brief.description || ""
                            )}&isOnboardingBrief=${
                              brief.isOnboardingBrief
                            }&hideArticle=${
                              Object.keys(userPlan).length === 0 &&
                              brief?.isOnboardingBrief &&
                              !brief?.isSample
                            }`}
                          ></Button>
                        </Box>
                      )}
                      {/* Article is completed from brief data */}
                      {!articleProgress[brief.id] &&
                        brief.articleStatus === "completed" && (
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              component="a"
                              sx={{
                                border: "none",
                                outline: "none",
                                borderRadius: "10px",
                                background:
                                  "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
                                position: "relative",
                                zIndex: "1",
                                "&::before": {
                                  content: "''",
                                  position: "absolute",
                                  top: "1px",
                                  right: "1px",
                                  bottom: "1px",
                                  left: "1px",
                                  backgroundColor: "#fff",
                                  borderRadius: "9px",
                                  zIndex: "-1",
                                },
                                "&::after": {
                                  content: "'Open AI Article'",
                                  background:
                                    "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
                                  WebkitBackgroundClip: "text",
                                  color: "transparent",
                                },
                                "&:hover": {
                                  border: "0px",
                                },
                              }}
                              onClick={async (e) => {
                                // Only prevent default for left-click to allow right-click context menu
                                if (e.button === 0) {
                                  e.preventDefault();
                                  await handleDownloadArticle(
                                    brief.id,
                                    brief.title
                                  );
                                }
                              }}
                              onAuxClick={async (e) => {
                                // Middle mouse button click
                                if (e.button === 1) {
                                  e.preventDefault();

                                  // Get the brief data
                                  const result = await dispatch(
                                    getArticle({ id: brief.id })
                                  );
                                  const data = result.payload?.data;

                                  // Calculate hideArticle value using the same logic as handleDownloadArticle
                                  let hideArticle = false;
                                  if (
                                    Object.keys(userPlan).length === 0 &&
                                    brief?.isOnboardingBrief &&
                                    !brief?.isSample
                                  ) {
                                    hideArticle = true;
                                  }

                                  // Check if article is ready
                                  if (
                                    data === null ||
                                    (data && data.status !== "completed")
                                  ) {
                                    dispatch(
                                      snackAdded({
                                        message:
                                          "Article is not ready to download",
                                        type: "error",
                                        duration: 2500,
                                      })
                                    );
                                    return;
                                  }

                                  // Open in new tab with the correct hideArticle value
                                  window.open(
                                    `/app/dashboard/articleEditor/?id=${encodeURIComponent(brief.id)}&title=${encodeURIComponent(brief.title || '')}&description=${encodeURIComponent(brief.description || '')}&isOnboardingBrief=${brief.isOnboardingBrief}&hideArticle=${hideArticle}`,
                                    "_blank"
                                  );
                                }
                              }}
                              href={`/app/dashboard/articleEditor/?id=${encodeURIComponent(
                                brief.id
                              )}&title=${encodeURIComponent(brief.title || '')}&description=${encodeURIComponent(
                                brief.description || ''
                              )}&isOnboardingBrief=${
                                brief.isOnboardingBrief
                              }&hideArticle=${
                                Object.keys(userPlan).length === 0 &&
                                brief?.isOnboardingBrief &&
                                !brief?.isSample
                              }`}
                            ></Button>
                          </Box>
                        )}

                      {/* Article is processing with real-time progress data */}
                      {(articleProgress[brief.id]?.status === "processing" ||
                        (brief.articleStatus === "processing" &&
                          !articleProgress[brief.id]?.status)) && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          {articleProgress[brief.id]?.current &&
                          articleProgress[brief.id]?.total ? (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Box sx={{ width: "100%", mr: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.round(
                                      (articleProgress[brief.id].current /
                                        articleProgress[brief.id].total) *
                                        100
                                    )}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {`${Math.round(
                                      (articleProgress[brief.id].current /
                                        articleProgress[brief.id].total) *
                                        100
                                    )}%`}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Box sx={{ width: "100%", mr: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.round((5 / 100) * 100)}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {`${Math.round((5 / 100) * 100)}%`}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      )}

                      {/* Article failed */}
                      {(articleProgress[brief.id]?.status === "failed" ||
                        brief.articleStatus === "failed") && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => handleRetryArticle(brief.id)}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}
                      {/* brief not completed */}
                      {(briefProgress[brief.id]?.stepRemains > 0 ||
                        brief?.stepRemains > 0) &&
                      ((brief?.stepRemains != undefined &&
                        brief?.stepRemains != null) ||
                        (briefProgress[brief.id]?.stepRemains != undefined &&
                          briefProgress[brief.id]?.stepRemains != null)) ? (
                        // Horizontally center the loader
                        <></>
                      ) : (
                        brief?.articleStatus === "Not Written" &&
                        !brief?.retry &&
                        (brief?.stepRemains === undefined ||
                          brief?.stepRemains === null ||
                          brief?.stepRemains <= 0) &&
                        (briefProgress[brief.id]?.stepRemains === undefined ||
                          briefProgress[brief.id]?.stepRemains === null ||
                          briefProgress[brief.id]?.stepRemains <= 0) && (
                          // Horizontally center the loader
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Link href={`/brief/${brief.id}`} passHref>
                              <Button variant="outlined" color="primary">
                                Write Article
                              </Button>
                            </Link>
                          </Box>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                </Fade>
              ))
            )}
          </TableBody>
        </Table>
        <Stack alignItems="center">
          <TablePagination
            component="div"
            count={total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default DashboardBriefTable;
